import { create } from 'zustand';

type SelectOption = { id: number; label: string; value: string };
type PlantSelectOption = { id: number; label: string; value: string; identifier: string };

interface ConsumerStoreState {
  dateRange: [Date | null, Date | null];
  setDateRange: (dateRange: [Date | null, Date | null]) => void;
  company: SelectOption | null;
  setCompany: (company: SelectOption) => void;
  cups: PlantSelectOption[];
  setCups: (cups: PlantSelectOption[]) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  shouldLoadData: boolean;
  setShouldLoadData: (should: boolean) => void;
  queryCounter: number;
  incrementQueryCounter: () => void;
  setCompaniesWithCupsCleanup: (
    company: SelectOption | null,
    allCups: Array<{ id: number; identifier: string; alias: string; companyId: number }>
  ) => void;
}

const useConsumerStore = create<ConsumerStoreState>((set, get) => ({
  dateRange: [null, null],
  setDateRange: (dateRange) => set({ dateRange }),
  company: null,
  setCompany: (company) => set({ company }),
  cups: [],
  setCups: (cups) => set({ cups }),
  isLoading: false,
  setIsLoading: (loading) => set({ isLoading: loading }),
  shouldLoadData: false,
  setShouldLoadData: (should) => set({ shouldLoadData: should }),
  queryCounter: 0,
  incrementQueryCounter: () => set((state) => ({ queryCounter: state.queryCounter + 1 })),
  setCompaniesWithCupsCleanup: (company, allCups) => {
    const currentCups = get().cups;

    // Filter out CUPS that don't belong to any of the selected companies
    const validCups = currentCups.filter((selectedCup) => {
      const cupData = allCups.find((cup) => cup.id === selectedCup.id);
      return cupData && company && company.id === cupData.companyId;
    });

    // Get all available CUPS for the selected company
    const availableCups = company ? allCups.filter((cup) => cup.companyId === company.id) : [];

    // Auto-select if only one CUPS is available
    const cupsToSet =
      availableCups.length === 1
        ? [
            {
              id: availableCups[0].id,
              label: availableCups[0].alias,
              value: availableCups[0].alias,
              identifier: availableCups[0].identifier,
            },
          ]
        : validCups;

    set({
      company,
      cups: cupsToSet,
    });
  },
}));

export default useConsumerStore;
